import { creatorbase, influencer, Operator, OperatorFilter, serializeOperatorFilterToApiParam } from '@round/api';
import { UrlState } from '../hooks/useCreatorExploreUrlState';
import {
    AverageDraftTimeOption,
    LastUsedTimeOption,
    PostingFrequencyTier,
    getLastUsedOptionFromStoredDate,
    getPostingFrequencyTierFromUrlState,
    maxAverageDraftTimeOptions,
} from './creatorSearchUtils';
import { ContentTagItem, LocationItem } from '../components/filters';
import { countryOptions } from 'utility/constants';
import { getTiktokIDFromIdPath } from '../components/filters/TiktokAudioUrlFilter/helpers';
export type Range = { min?: number; max?: number };

export const minMax = (min: number | undefined, max: number | undefined): Range | undefined =>
    typeof min === 'number' || typeof max === 'number' ? { min, max } : undefined;

export type PlatformsFilter = OperatorFilter<Extract<creatorbase.Platform, 'tiktok' | 'instagram'>>;

export type AudienceLocationCondition = { countryCode: string; minPercentage: number | undefined };

export type ContentTagsFilter = OperatorFilter<ContentTagItem>;

export type Filters = {
    platforms: PlatformsFilter | undefined;
    contentTags: ContentTagsFilter | undefined;
    locations: LocationItem[] | undefined;
    audienceLocations: OperatorFilter<AudienceLocationCondition> | undefined;
    followerRange: Range | undefined;
    avgViews: Range | undefined;
    avgEngagementRate: Range | undefined;
    estimatedCpe: Range | undefined;
    responseRate: number | undefined;
    avgDraftTime: AverageDraftTimeOption | undefined;
    lastUsed: LastUsedTimeOption | undefined;
    postingFrequency: PostingFrequencyTier | undefined;
    costRange: Range | undefined;
    hasCost: boolean | undefined;
    tiktokAudioIds: string[] | undefined;
    spotifyTrackIds: string[] | undefined;
    spotifyGenres: string[] | undefined;
    spotifyArtistIds: string[] | undefined;
};

export const getActiveFilterCount = (filters: Partial<Filters>): number =>
    Object.values(filters).filter((value) => {
        if (Array.isArray(value)) {
            return value.length > 0;
        }

        return Boolean(value);
    }).length;

/**
 * Serialize an operator filter to a concise url-friendly string.
 *
 * Format: `operator:val1,val2` (e.g. `and:tiktok,instagram`, `or:tiktok,instagram`)
 * No operator: plain `val1,val2`
 *
 * Optionally provide a `serializeCondition` function to convert each condition to a string.
 * Defaults to `String()`.
 */
export function serializeOperatorFilterToUrl<T = string>(
    filter: OperatorFilter<T> | undefined,
    serializeCondition?: (condition: T) => string
): string | undefined {
    const serialize = serializeCondition ?? String;
    const { operator, conditions } = filter || {};

    if (!conditions?.length) {
        return undefined;
    }

    const values = conditions.map(serialize).filter(Boolean);

    if (!values.length) {
        return undefined;
    }

    return operator === undefined
        ? // Serialize as regular values. Default operator could be contextual
          values.join(',')
        : `${operator}:${values.join(',')}`;
}

/**
 * Parse an operator filter URL param into structured form.
 *
 * Optionally provide a `parseCondition` function to convert each raw string to a condition.
 * Defaults to identity (returns the raw string as-is).
 */
export function parseOperatorFilterFromUrl<T = string>(
    value: string | undefined,
    parseCondition?: (raw: string) => T
): OperatorFilter<T> | undefined {
    const parse = parseCondition ?? ((s: string) => s as T);
    if (!value) {
        return undefined;
    }

    const separatorIndex = value.indexOf(':');
    const validOperators: Operator[] = ['OR', 'AND'];

    let operator;
    const parsedOperator = value.slice(0, separatorIndex);

    if (parsedOperator && validOperators.includes(parsedOperator as Operator)) {
        operator = parsedOperator as Operator;
    }

    const rawConditions = (operator ? value.slice(separatorIndex + 1) : value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

    const conditions = rawConditions.map(parse);

    if (conditions.length) {
        return { operator, conditions };
    } else {
        return undefined;
    }
}

export const serializeAudienceLocationCondition = (condition: AudienceLocationCondition): string =>
    `${condition.countryCode}|${condition.minPercentage}`;

export const parseAudienceLocationUrlCondition = (raw: string): AudienceLocationCondition => {
    const [countryCode, minStr] = raw.split('|');
    const minAsNumber = Number(minStr);
    return { countryCode, minPercentage: Number.isFinite(minAsNumber) ? minAsNumber : undefined };
};

export const serializeContentTagsToApi = (filter: ContentTagsFilter | undefined): string | undefined =>
    serializeOperatorFilterToApiParam(
        filter?.conditions.length
            ? { operator: filter.operator, conditions: filter.conditions.map((tag) => tag.value) }
            : undefined
    );

export const parseContentTagsFromApi = (value: string | undefined): ContentTagsFilter | undefined => {
    if (!value?.trim()) {
        return undefined;
    }

    const trimmed = value.trim();
    if (trimmed.startsWith('{')) {
        try {
            const data = JSON.parse(trimmed) as { operator?: Operator; conditions?: unknown };
            if (!Array.isArray(data.conditions)) {
                return undefined;
            }
            const conditions = data.conditions
                .map((tag) => String(tag).trim())
                .filter(Boolean)
                .map((tag): ContentTagItem => ({ value: tag, label: tag }));
            if (!conditions.length) {
                return undefined;
            }
            return { operator: data.operator, conditions };
        } catch {
            return undefined;
        }
    }

    return parseOperatorFilterFromUrl(trimmed, (tag) => ({ value: tag, label: tag }));
};

export const serializeAudienceLocationsToApi = (
    filter: OperatorFilter<AudienceLocationCondition> | undefined
): string | undefined => {
    const validConditions = filter?.conditions.filter((c) => c.countryCode && typeof c.minPercentage === 'number');
    if (!validConditions?.length) {
        return undefined;
    }

    return JSON.stringify({
        operator: filter?.operator,
        conditions: validConditions.map((c) => ({ country: c.countryCode, min_percentage: c.minPercentage })),
    });
};

export const mapFiltersToApiParams = (
    filters: Partial<Filters>
): Pick<
    influencer.GetCreatorExploreParams,
    | 'platforms'
    | 'content_tags'
    | 'primary_country'
    | 'audience_location'
    | 'min_follower_count'
    | 'max_follower_count'
    | 'min_avg_video_views_per_post'
    | 'max_avg_video_views_per_post'
    | 'min_estimated_cpe'
    | 'max_estimated_cpe'
    | 'min_avg_engagement_rate'
    | 'max_avg_engagement_rate'
    | 'min_response_rate'
    | 'max_avg_draft_time_in_hours'
    | 'min_last_used_date'
    | 'min_cost'
    | 'max_cost'
    | 'has_cost'
    | 'has_used_tiktok_audio_tiktok_ids'
    | 'spotify_ids'
    | 'genres'
    | 'artist_ids'
    | 'posting_frequency'
> => ({
    platforms: filters.platforms ? serializeOperatorFilterToApiParam(filters.platforms) : undefined,
    content_tags: serializeContentTagsToApi(filters.contentTags),
    primary_country: filters.locations?.map((l) => l.value).join(','),
    audience_location: serializeAudienceLocationsToApi(filters.audienceLocations),
    min_follower_count: filters.followerRange?.min,
    max_follower_count: filters.followerRange?.max,
    min_avg_video_views_per_post: filters.avgViews?.min,
    max_avg_video_views_per_post: filters.avgViews?.max,
    min_estimated_cpe: filters.estimatedCpe?.min,
    max_estimated_cpe: filters.estimatedCpe?.max,
    min_response_rate: filters.responseRate ? (filters.responseRate / 100).toString() : undefined,
    min_avg_engagement_rate: filters.avgEngagementRate?.min ? filters.avgEngagementRate.min / 100 : undefined,
    max_avg_engagement_rate: filters.avgEngagementRate?.max ? filters.avgEngagementRate.max / 100 : undefined,
    max_avg_draft_time_in_hours: filters.avgDraftTime ? String(filters.avgDraftTime.value) : undefined,
    min_last_used_date: filters.lastUsed?.value,
    min_cost: filters.costRange?.min,
    max_cost: filters.costRange?.max,
    has_cost: filters.hasCost ? 'true' : undefined,
    has_used_tiktok_audio_tiktok_ids: filters.tiktokAudioIds?.map((id) => getTiktokIDFromIdPath(id)).join(','),
    spotify_ids: filters.spotifyTrackIds?.join(','),
    genres: filters.spotifyGenres?.join(','),
    artist_ids: filters.spotifyArtistIds?.join(','),
    posting_frequency: filters.postingFrequency,
});

export const getFiltersFromUrlState = (urlState: UrlState): Filters => ({
    platforms: parseOperatorFilterFromUrl<Extract<creatorbase.Platform, 'tiktok' | 'instagram'>>(urlState.platforms),
    contentTags: parseContentTagsFromApi(urlState.contentTags),
    locations:
        urlState.locations?.map((code) => {
            const option = countryOptions.find((c) => c.value === code);
            return { value: code, label: option?.label ?? code };
        }) ?? [],
    audienceLocations: parseOperatorFilterFromUrl<AudienceLocationCondition>(
        urlState.audienceLocations,
        parseAudienceLocationUrlCondition
    ),
    responseRate: urlState.minResponseRate,
    avgDraftTime: maxAverageDraftTimeOptions.find((o) => o.value === urlState.maxAvgDraftTimeInHours),
    lastUsed: getLastUsedOptionFromStoredDate(urlState.maxLastUsedTime),
    postingFrequency: getPostingFrequencyTierFromUrlState(urlState.postingFrequency),
    hasCost: urlState.hasCost,
    tiktokAudioIds: urlState.tiktokAudioIds,
    spotifyTrackIds: urlState.spotifyTrackIds,
    spotifyGenres: urlState.spotifyGenres,
    spotifyArtistIds: urlState.spotifyArtistIds,
    followerRange: minMax(urlState.minFollowers, urlState.maxFollowers),
    avgViews: minMax(urlState.minAvgViews, urlState.maxAvgViews),
    avgEngagementRate: minMax(urlState.minAvgEngagementRate, urlState.maxAvgEngagementRate),
    estimatedCpe: minMax(urlState.minEstimatedCpe, urlState.maxEstimatedCpe),
    costRange: minMax(urlState.minCost, urlState.maxCost),
});

export const mapFiltersToUrlState = (filters: Filters): Partial<UrlState> => ({
    platforms: serializeOperatorFilterToUrl(filters.platforms),
    contentTags: serializeContentTagsToApi(filters.contentTags),
    locations: filters.locations?.map((location) => location.value),
    audienceLocations: serializeOperatorFilterToUrl(filters.audienceLocations, serializeAudienceLocationCondition),
    minFollowers: filters.followerRange?.min,
    maxFollowers: filters.followerRange?.max,
    minAvgViews: filters.avgViews?.min,
    maxAvgViews: filters.avgViews?.max,
    minAvgEngagementRate: filters.avgEngagementRate?.min,
    maxAvgEngagementRate: filters.avgEngagementRate?.max,
    minEstimatedCpe: filters.estimatedCpe?.min,
    maxEstimatedCpe: filters.estimatedCpe?.max,
    minResponseRate: filters.responseRate,
    maxAvgDraftTimeInHours: filters.avgDraftTime?.value,
    maxLastUsedTime: filters.lastUsed?.value,
    postingFrequency: filters.postingFrequency,
    minCost: filters.costRange?.min,
    maxCost: filters.costRange?.max,
    hasCost: filters.hasCost,
    tiktokAudioIds: filters.tiktokAudioIds,
    spotifyTrackIds: filters.spotifyTrackIds,
    spotifyGenres: filters.spotifyGenres,
    spotifyArtistIds: filters.spotifyArtistIds,
});
